import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Receipt } from 'lucide-react';
import { ServiceRequestType, VENUE_EVENTS, rooms } from '@/lib/shared-types';
import { formatCurrency } from '@/lib/i18n';
import { raiseServiceRequest } from '@/lib/mutations';
import { errorMessage, GUEST_FALLBACK_CURRENCY } from '@/lib/guest';
import { loadGuestChecks, type GuestCheck } from '@/lib/checks';
import { useRealtime } from '@/Hooks/useRealtime';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAppSelector } from '@/Redux/store';

/**
 * The guest's own bill: what has been charged, what is left to
 * pay, and the one-tap request that puts a REQUEST_BILL in the waiter's queue.
 */
export default function Bill() {
  const { t, i18n } = useTranslation();
  const session = useAppSelector((s) => s.guest.session);
  const [checks, setChecks] = useState<GuestCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(() => {
    if (!session?.sessionId) return;
    loadGuestChecks(session.sessionId)
      .then(setChecks)
      .catch(() => setChecks([]))
      .finally(() => setLoading(false));
  }, [session?.sessionId]);

  useEffect(load, [load]);

  useRealtime(
    VENUE_EVENTS.CHECK_UPDATED,
    load,
    session?.sessionId ? [rooms.guest(session.sessionId)] : [],
  );

  /** The same request a waiter sees in their queue, raised from here. */
  const requestBill = async () => {
    setRequesting(true);
    try {
      // Durable before the network. `sessionId` comes from the token.
      const { synced } = await raiseServiceRequest(ServiceRequestType.REQUEST_BILL);
      toast.success(synced ? t('guest.bill.requested') : t('guest.sync.queued'));
    } catch (error) {
      toast.error(errorMessage(error, t('guest.bill.requestFailed')));
    } finally {
      setRequesting(false);
    }
  };

  const currency = checks[0]?.currency ?? session?.currency ?? GUEST_FALLBACK_CURRENCY;
  const money = (value: string | number) => formatCurrency(value, currency, i18n.language);

  if (loading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  if (!checks.length) {
    return <p className="py-16 text-center text-sm text-muted-foreground">{t('guest.bill.empty')}</p>;
  }

  return (
    /* The same grid the order list uses. One visit can hold several checks --
       the table's master bill plus whatever the till split off it -- and
       stacking them put a single narrow column down the middle of anything
       wider than a phone, with the check you were looking for below the fold.
       `items-start` so a two-line check does not stretch to match a twelve-line
       one beside it. */
    <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {checks.map((check) => {
          const live = (check.charges ?? []).filter((charge) => !charge.voidedAt);
          const due = Number(check.grandTotal) - Number(check.paidTotal);

          return (
            <Card key={check.id}>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">#{check.number}</span>
                  {check.isMaster && (
                    <span className="rounded-full border border-line bg-accent-soft px-2.5 py-0.5 text-xs font-medium text-accent">
                      {t('guest.bill.master')}
                    </span>
                  )}
                </div>

                {live.length ? (
                  <ul className="space-y-1.5 border-t pt-3 text-sm">
                    {live.map((charge) => (
                      <li key={charge.id} className="flex items-start justify-between gap-3">
                        <span className="min-w-0 truncate">{charge.description}</span>
                        <span className="shrink-0 tabular-nums">{money(charge.amount)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="border-t pt-3 text-sm text-muted-foreground">{t('guest.bill.empty')}</p>
                )}

                <dl className="space-y-1 border-t pt-3 text-sm">
                  <Row label={t('common.subtotal')} value={money(check.subtotal)} />
                  {Number(check.discountTotal) !== 0 && (
                    <Row label={t('common.discount')} value={`- ${money(check.discountTotal)}`} />
                  )}
                  {Number(check.serviceTotal) !== 0 && (
                    <Row label={t('guest.bill.service')} value={money(check.serviceTotal)} />
                  )}
                  {Number(check.taxTotal) !== 0 && (
                    <Row label={t('common.tax')} value={money(check.taxTotal)} />
                  )}
                  {Number(check.tipTotal) !== 0 && (
                    <Row label={t('common.tip')} value={money(check.tipTotal)} />
                  )}
                  <Row
                    label={t('common.total')}
                    value={money(check.grandTotal)}
                    className="font-semibold"
                  />
                  {Number(check.paidTotal) > 0 && (
                    <>
                      <Row label={t('guest.bill.paid')} value={money(check.paidTotal)} />
                      <Row
                        label={t('guest.bill.due')}
                        value={money(due)}
                        className="font-semibold"
                      />
                    </>
                  )}
                </dl>
              </CardContent>
            </Card>
          );
      })}

      <Button
        className="w-full"
        size="lg"
        disabled={requesting}
        onClick={() => void requestBill()}
      >
        <Receipt className="h-5 w-5" />
        {requesting ? t('common.loading') : t('guest.bill.request')}
      </Button>
    </div>
  );
}

function Row({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={`flex justify-between gap-3 ${className ?? ''}`}>
      <dt>{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
